import { Snackbar } from 'components/Snackbar';
import { IUseDisclose, useDisclose, useLoader } from 'hooks/utilHooks';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { TreatmentConsultationService } from 'services';
import {
  ICreateTreatmentConsultation,
  IFindTreatmentConsultationParams,
  ITreatmentConsultation,
  ITreatmentConsultationFilter,
  ITreatmentConsultationExam,
  ITreatmentConsultationResult,
  ITreatmentPrescription,
} from 'types';

import { AlertColor } from '@mui/material';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

interface ISnackbar {
  message: string | null;
  variant?: AlertColor | undefined;
}

export interface ITreatmentConsultationContext
  extends ITreatmentConsultationResult {
  loading: boolean;
  saveTreatmentConsultationModal: IUseDisclose;
  saveTreatmentPrescriptionModal: IUseDisclose;
  currentTreatmentConsultation: ITreatmentConsultation | null;
  selectedTreatmentConsultation: ITreatmentConsultation | null;
  selectedPrescription: ITreatmentPrescription | undefined;
  exams: ITreatmentConsultationExam[];
  treatmentOnGoingId: string;
  handleChangeSelectedConsultation: (
    consultation: ITreatmentConsultation | null
  ) => void;
  handleChangeSelectedPrescription: (
    prescription: ITreatmentPrescription | undefined
  ) => void;
  saveConsultation: (
    consultation: ICreateTreatmentConsultation
  ) => Promise<void>;
  savePrescription: (
    prescription: Pick<ICreateTreatmentConsultation, 'prescription'>
  ) => Promise<void>;
  handleNextPage: () => void;
  handlePreviousPage: () => void;
  handleRefresh: () => void;
}

export const TreatmentConsultationContext =
  createContext<ITreatmentConsultationContext>(
    {} as ITreatmentConsultationContext
  );

type Props = {
  children: ReactNode;
  petId: string;
  treatmentOnGoingId: string;
};

export const TreatmentConsultationProvider = ({
  children,
  petId,
  treatmentOnGoingId,
}: Props) => {
  const [treatmentConsultations, setTreatmentConsultations] =
    useState<ITreatmentConsultationResult>({
      docs: [],
    } as unknown as ITreatmentConsultationResult);

  const [currentTreatmentConsultation, setCurrentTreatmentConsultation] =
    useState<ITreatmentConsultation | null>(null);

  const [selectedTreatmentConsultation, setSelectedTreatmentConsultation] =
    useState<ITreatmentConsultation | null>(null);

  const [selectedPrescription, setSelectedPrescription] = useState<
    ITreatmentPrescription | undefined
  >();

  const [exams, setExams] = useState<ITreatmentConsultationExam[]>([]);

  const { showLoader, hideLoader, loading } = useLoader(false);

  const saveTreatmentConsultationModal = useDisclose();
  const saveTreatmentPrescriptionModal = useDisclose();

  const filterForm = useForm<ITreatmentConsultationFilter>({
    defaultValues: { startDate: null, endDate: null },
  });

  const [startDate, endDate] = useWatch({
    control: filterForm.control,
    name: ['startDate', 'endDate'],
  });

  const [snackbar, setSnackbar] = useState<ISnackbar>({
    message: '',
    variant: 'info',
  });

  const findAllByPetId = async ({
    petId,
    page,
    limit,
  }: IFindTreatmentConsultationParams) => {
    if (!petId) {
      return;
    }

    try {
      showLoader();

      await TreatmentConsultationService.findAllByPetId({
        petId,
        limit,
        page,
        startDate,
        endDate,
      }).then(setTreatmentConsultations);

      await TreatmentConsultationService.findByTreatmentOnGoingId(
        treatmentOnGoingId
      ).then(setCurrentTreatmentConsultation);
    } catch (reason) {
      const { message } = reason as Error;

      setSnackbar({
        message,
        variant: 'error',
      });
    } finally {
      hideLoader();
    }
  };

  const saveConsultation = async (
    consultation: ICreateTreatmentConsultation
  ) => {
    try {
      showLoader();

      await TreatmentConsultationService.saveConsultation(consultation);

      setSnackbar({
        message: 'Consulta salva com sucesso',
        variant: 'success',
      });

      saveTreatmentConsultationModal.onClose();
    } catch (reason) {
      const { message } = reason as Error;

      setSnackbar({
        message,
        variant: 'error',
      });
    } finally {
      hideLoader();
      findAllByPetId({ petId });
    }
  };

  const savePrescription = async ({
    prescription,
  }: Pick<ICreateTreatmentConsultation, 'prescription'>) => {
    try {
      if (!currentTreatmentConsultation?.id) {
        throw new Error('Não foi possível salvar a prescrição.');
      }

      showLoader();

      await TreatmentConsultationService.savePrescription({
        prescription,
        id: currentTreatmentConsultation?.id,
      });

      setSnackbar({
        message: 'Prescrição salva com sucesso',
        variant: 'success',
      });

      saveTreatmentPrescriptionModal.onClose();
    } catch (reason) {
      const { message } = reason as Error;

      setSnackbar({
        message,
        variant: 'error',
      });
    } finally {
      hideLoader();
      findAllByPetId({ petId });
    }
  };

  const handlePreviousPage = () => {
    findAllByPetId({
      petId,
      page: treatmentConsultations.prevPage,
    });
  };

  const handleNextPage = () => {
    findAllByPetId({
      petId,
      page: treatmentConsultations.nextPage,
    });
  };

  const handleRefresh = () => {
    findAllByPetId({ petId });
  };

  const handleChangeSelectedConsultation = (
    consultation: ITreatmentConsultation | null
  ) => {
    setSelectedTreatmentConsultation(consultation);
  };

  const handleChangeSelectedPrescription = (
    prescription: ITreatmentPrescription | undefined
  ) => {
    setSelectedPrescription(prescription);
  };

  useEffect(() => {
    findAllByPetId({ petId, endDate, startDate });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petId, startDate, endDate]);

  const findAllExams = async () => {
    try {
      showLoader();

      await TreatmentConsultationService.findAllExams().then(setExams);
    } catch (reason) {
      const { message } = reason as Error;

      setSnackbar({
        message,
        variant: 'error',
      });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    findAllExams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <TreatmentConsultationContext.Provider
      value={{
        loading,
        currentTreatmentConsultation,
        selectedTreatmentConsultation,
        selectedPrescription,
        treatmentOnGoingId,
        saveTreatmentConsultationModal,
        saveTreatmentPrescriptionModal,
        exams,
        handleChangeSelectedConsultation,
        handleChangeSelectedPrescription,
        saveConsultation,
        savePrescription,
        handleNextPage,
        handlePreviousPage,
        handleRefresh,
        ...treatmentConsultations,
      }}
    >
      <FormProvider {...filterForm}>{children}</FormProvider>

      <Snackbar
        text={snackbar.message}
        snackbarOpen={Boolean(snackbar.message)}
        setSnackbarOpen={() => {
          setSnackbar({
            message: '',
          });
        }}
        severityType={snackbar.variant}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      />
    </TreatmentConsultationContext.Provider>
  );
};

export const useTreatmentConsultation = () => {
  return useContext(TreatmentConsultationContext);
};
